import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Calendar, Gauge, Fuel, Cog, Zap, MapPin, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { imgUrl, euro, km, useApi, errMsg } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { SellerBadge, ModeBadge } from "@/components/SellerBadge";
import { OfferDialog } from "@/components/OfferDialog";
import { AttachmentList } from "@/components/Attachments";
import { DeliveryPicker, ShippingInfo, deliveryValid, SHIPPING_PRICE, CONTACT_EMAIL } from "@/components/Delivery";

const Spec = ({ icon: Icon, label, value }) => (
  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
    <Icon size={16} className="text-slate-400" />
    <p className="text-xs text-slate-500 uppercase tracking-wider mt-2">{label}</p>
    <p className="font-semibold text-slate-900">{value}</p>
  </div>
);

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const api = useApi();
  const { me, isSignedIn, isLoaded, favIds, toggleFavorite } = useUser();
  const [l, setL] = useState(null);
  const [active, setActive] = useState(0);
  const [offerOpen, setOfferOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [delivery, setDelivery] = useState({ delivery: "ritiro", address: "" });
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api("get", `/listings/${id}`).then(setL).catch(() => setMissing(true));
  }, [id, api]);

  if (missing) return <div className="max-w-3xl mx-auto py-24 text-center" data-testid="listing-not-found"><p className="font-heading text-2xl">Annuncio non trovato</p></div>;
  if (!l) return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-500">Caricamento...</div>;

  const mine = me && (me.user_id === l.owner_id || me.role === "admin");
  const available = isLoaded && l.status === "active" && !mine;
  const needLogin = (fn) => () => {
    if (!isLoaded) return;
    isSignedIn ? fn() : nav("/accedi");
  };

  const buy = async () => {
    try {
      await api("post", `/listings/${l.id}/buy`, delivery);
      toast.success("Acquisto confermato! Il venditore ti contatterà.");
      nav("/area-personale?tab=acquisti");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/cerca" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="detail-back-link"><ArrowLeft size={16} />Torna alla ricerca</Link>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_400px] gap-10">
        <div className="min-w-0">
          <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-slate-100 relative">
            <img src={imgUrl(l.images?.[active])} alt={l.model} className="w-full h-full object-cover" data-testid="detail-main-image" />
            {l.status === "sold" && <div className="absolute inset-0 bg-slate-900/60 grid place-items-center text-white font-heading text-4xl font-bold" data-testid="detail-sold-overlay">VENDUTO</div>}
          </div>
          {l.images?.length > 1 && (
            <div className="flex gap-3 mt-3 overflow-x-auto">
              {l.images.map((img, i) => (
                <button key={img} onClick={() => setActive(i)} data-testid={`detail-thumb-${i}`}
                  className={`w-24 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === active ? "border-blue-600" : "border-transparent"}`}>
                  <img src={imgUrl(img)} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
            <Spec icon={Calendar} label="Anno" value={l.year} />
            <Spec icon={Gauge} label="Chilometraggio" value={km(l.km)} />
            <Spec icon={Fuel} label="Alimentazione" value={l.fuel} />
            <Spec icon={Cog} label="Cambio" value={l.gearbox} />
            <Spec icon={Zap} label="Potenza" value={l.power_cv ? `${l.power_cv} CV` : "—"} />
            <Spec icon={MapPin} label="Città" value={l.city} />
          </div>
          <div className="mt-8">
            <h2 className="font-heading text-xl font-semibold">Descrizione</h2>
            <p className="text-slate-600 mt-3 whitespace-pre-line leading-relaxed" data-testid="detail-description">{l.description || "Nessuna descrizione."}</p>
          </div>
          <AttachmentList listingId={l.id} items={l.attachments} />
          <p className="mt-8 text-sm text-slate-600" data-testid="detail-contact">Per informazioni: <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 font-medium">{CONTACT_EMAIL}</a></p>
        </div>

        <aside className="lg:sticky lg:top-24 self-start bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 flex-wrap"><SellerBadge type={l.seller_type} /><ModeBadge offers={l.accept_offers} /></div>
          <p className="text-xs uppercase tracking-wider text-slate-500 mt-5">{l.brand}</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="detail-title">{l.model}</h1>
          <p className="font-mono text-4xl text-slate-900 mt-4" data-testid="detail-price">{euro(l.price)}</p>
          {l.min_offer && l.accept_offers && <p className="text-xs text-slate-500 mt-1">Offerte a partire da {euro(l.min_offer)}</p>}
          <div className="mt-5"><ShippingInfo /></div>
          <p className="text-sm text-slate-600 mt-4">Venduto da <span className="font-semibold text-slate-900" data-testid="detail-seller-name">{l.seller_name}</span></p>

          <div className="mt-6 space-y-3">
            {mine && <p className="text-sm p-3 rounded-lg bg-slate-50 border text-slate-600" data-testid="detail-own-listing-note">Sei l'amministratore: gestisci questo annuncio dal pannello.</p>}
            <Button data-testid="listing-card-compra-subito-button" disabled={!available} onClick={needLogin(() => setBuyOpen(true))}
              className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-base">Compra subito · {euro(l.price)}</Button>
            {l.accept_offers && (
              <Button data-testid="listing-card-fai-offerta-button" disabled={!available} onClick={needLogin(() => setOfferOpen(true))} variant="outline"
                className="w-full h-12 rounded-full border-amber-500 text-amber-700 hover:bg-amber-50 text-base">Fai un'offerta al ribasso</Button>
            )}
            <Button data-testid="listing-favorite-toggle-button" variant="ghost" className="w-full rounded-full" onClick={() => toggleFavorite(l.id)}>
              <Heart size={16} className="mr-2" fill={favIds.includes(l.id) ? "currentColor" : "none"} />
              {favIds.includes(l.id) ? "Salvato nei preferiti" : "Salva nei preferiti"}
            </Button>
          </div>
        </aside>
      </div>

      {l.accept_offers && <OfferDialog listing={l} open={offerOpen} onOpenChange={setOfferOpen} />}
      <AlertDialog open={buyOpen} onOpenChange={setBuyOpen}>
        <AlertDialogContent data-testid="buy-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Confermi l'acquisto?</AlertDialogTitle>
            <AlertDialogDescription>{l.brand} {l.model}: prezzo veicolo {euro(l.price)}. Il veicolo verrà riservato per te.</AlertDialogDescription>
          </AlertDialogHeader>
          <DeliveryPicker value={delivery} onChange={setDelivery} />
          <div className="flex justify-between items-center p-3 rounded-xl bg-slate-50 border text-sm" data-testid="buy-total">
            <span>Totale</span>
            <span className="font-mono text-lg">{euro(l.price + (delivery.delivery === "spedizione" ? SHIPPING_PRICE : 0))}</span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="buy-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="buy-confirm-button" disabled={!deliveryValid(delivery)} onClick={buy} className="bg-blue-600 hover:bg-blue-700">Conferma acquisto</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
